import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'

/** One derivation the dialog asks about: the derived field's key, and its inputs' values. */
export interface DerivationRequest {
  keyName: string
  params: Record<string, string>
}

/**
 * One derivation's answer, matched back by `keyName` — never by position.
 *
 * Exactly one of `value` | `error` is meaningful: `value` is the typed scalar RPS projected (or
 * `null` when the upstream had nothing to say), `error` an item- or call-level failure.
 */
export interface DerivationResult {
  keyName: string
  value?: unknown
  error?: unknown
}

/**
 * Seam between the Create/Edit Transaction dialog and whatever answers its derivations, mirroring
 * {@link WorkbenchDataSource} and {@link TransactionValueSource} (PVAL-26685).
 *
 * The dialog no longer knows any URL: the served metadata carries `derivationInputs` only, so a
 * derivation is "this field, these input values" and the answer is the value to write. The real
 * implementation posts those as `type: "Derivation"` items to `dialog-metadata/resolve`
 * ({@link HttpTransactionDerivationService}); {@link StaticTransactionDerivationService} answers the
 * same shapes offline.
 */
export abstract class TransactionDerivationSource {
  /**
   * Resolve a batch of derivations in one call.
   *
   * Emits exactly once, one result per item it could answer. It must not throw: a failure is a
   * result carrying `error` (per item, or on every item when the whole call failed), so the dialog
   * can mark exactly the fields that did not fill.
   */
  abstract resolve(
    items: readonly DerivationRequest[],
    accountId: string,
  ): Observable<DerivationResult[]>
}

/**
 * Canned answers keyed by the derived field, so the wiring is exercisable with no backend — what
 * the panel binds when `USE_HTTP_TRANSACTION_DERIVATION` is off.
 */
export const STATIC_DERIVED_VALUES: Readonly<Record<string, unknown>> = {
  custodianAccountId: 144592,
  generalLedgerId: 4938,
  tradeCurrency: 'USD',
  settleCurrency: 'USD',
  'distributionLines.accountSubType': 'Asset',
  'distributionLines.currency': 'USD',
}

@Injectable()
export class StaticTransactionDerivationService extends TransactionDerivationSource {
  resolve(items: readonly DerivationRequest[]): Observable<DerivationResult[]> {
    // An unknown key answers `null` — "nothing to say" — which leaves the field alone, the same
    // outcome as an unfilled trigger.
    return of(
      items.map(item => ({
        keyName: item.keyName,
        value: STATIC_DERIVED_VALUES[item.keyName] ?? null,
      })),
    )
  }
}
